import classNames from 'classnames';
import { useMemo, useRef, useState, type RefObject } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import Image from '@alltrails/core/components/Image';
import useActivityStrings from '@alltrails/core/hooks/useActivityStrings';
import Button from '@alltrails/denali/components/Button';
import Checkbox from '@alltrails/denali/components/Checkbox';
import Dropdown from '@alltrails/denali/components/Dropdown';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import Select from '@alltrails/denali/components/Select';
import Switch from '@alltrails/denali/components/Switch';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import type { ActivityType } from '@alltrails/types/ActivityType';
import useLogMapOverlayDisplayed from '../../hooks/useLogMapOverlayDisplayed';
import { keycodeShortcuts } from '../../hooks/useStyleKeyboardShortcuts';
import useStyleSelection from '../../hooks/useStyleSelection';
import { useAppDispatch, useAppSelector } from '../../redux';
import { addOrRemoveBoundaryFilter, toggleHeatmapStyleId, updateAdminStyleSettings } from '../../redux/reducer';
import { BoundaryFilterType, StyleId } from '../../types/Styles';
import adminUrlIsValid from '../../utils/adminUrlIsValid';
import { baseStyleCardConfigs, dynamicStyleCardConfigs, staticStyleCardConfigs } from '../../utils/styleCardConfigs';
import { formatStyleDisplayName, isBaseStyle, isDynamicStyle, isStaticStyle, isStyleApplied } from '../../utils/styleIdHelpers';
import CommunityWaypointCategoryFilters from '../Waypoints/CommunityWaypointCategoryFilters';
import styles from './ExtraMenuItem.module.scss';
import { useExtraMenuItemIcon } from './useExtraMenuItemIcon';

const adminStyleSourceUrlId = 'adminOverlay';

type Props = {
  loggedOverlayIds?: RefObject<Set<StyleId>>;
  styleId: StyleId;
  onUrlButtonClick?: () => void;
} & Parameters<typeof useStyleSelection>[0];

const ExtraMenuItem = ({
  loggedOverlayIds,
  onUrlButtonClick,
  styleId,
  onMapBaseLayerTapped,
  onMapOverlayTapped,
  disableAnalytics,
  handleFailedEntitlementCheck,
  handleRoadblock
}: Props) => {
  const [boundaryTypeSelectOpen, setBoundaryTypeSelectOpen] = useState(false);
  const isMobile = useIsMobileSizedScreen();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { hasPermission } = useAuthorization();
  const isAdmin =
    hasPermission({ permission: 'alerts:manage' }) &&
    hasPermission({ permission: 'areas:manage' }) &&
    hasPermission({ permission: 'public_lands_onboarding:manage' });
  const isPLP = hasPermission({ permission: 'public_lands:manage' });
  const { adminStyleSettings, baseStyleId, displayMetric, dynamicStyleIds, heatmapStyleIds, staticStyleIds, trailId } = useAppSelector(state => ({
    adminStyleSettings: state.map.adminStyleSettings,
    baseStyleId: state.map.baseStyleId,
    displayMetric: state.context.displayMetric,
    dynamicStyleIds: state.map.dynamicStyleIds,
    heatmapStyleIds: state.map.heatmapStyleIds,
    staticStyleIds: state.map.staticStyleIds,
    trailId: state.map.trailId
  }));

  const { onStyleSelect } = useStyleSelection({
    onMapBaseLayerTapped,
    onMapOverlayTapped,
    disableAnalytics,
    handleFailedEntitlementCheck,
    handleRoadblock
  });
  const itemRef = useRef<HTMLDivElement>(null);
  useLogMapOverlayDisplayed({ disableAnalytics, itemRef, loggedOverlayIds, styleId });

  const active = isStyleApplied(styleId, { baseStyleId, dynamicStyleIds, staticStyleIds });

  const { displayName, imgSrc } = useMemo<{ displayName: string; imgSrc?: string }>(() => {
    if (isBaseStyle(styleId)) {
      const { displayName, imgSrc } = baseStyleCardConfigs[styleId];
      return { displayName: formatStyleDisplayName(intl, displayName), imgSrc };
    } else if (isStaticStyle(styleId)) {
      const { displayName, imgSrc } = staticStyleCardConfigs[styleId];
      return { displayName: formatStyleDisplayName(intl, displayName), imgSrc };
    } else {
      const { displayName, imgSrc, adminImgSrc, metricImgSrc } = dynamicStyleCardConfigs[styleId];

      let formattedDisplayName = formatStyleDisplayName(intl, displayName);
      if (styleId === 'heatmap') {
        // admins retain the ability to use "legacy" heatmaps in the "Extras" tab, with a new display name:
        if (trailId) {
          formattedDisplayName = intl.formatMessage({ defaultMessage: 'Attached recordings' });
        } else {
          formattedDisplayName = intl.formatMessage({ defaultMessage: 'Trail system' });
        }
      }

      let localizedImgSrc = imgSrc;
      if (styleId === 'distanceMarkers' && !isAdmin && displayMetric) {
        localizedImgSrc = metricImgSrc;
      } else if (isAdmin) {
        localizedImgSrc = adminImgSrc ?? imgSrc;
      }
      return { displayName: formattedDisplayName, imgSrc: localizedImgSrc };
    }
  }, [styleId, intl, isAdmin, displayMetric, trailId]);

  const heatmapCheckbox = useMemo(() => {
    if (isDynamicStyle(styleId) && dynamicStyleCardConfigs[styleId].hasHeatmap && (isAdmin || isPLP)) {
      return (
        <Checkbox
          className={styles.heatmapCheckbox}
          disabled={!dynamicStyleIds.includes(styleId)}
          id={styleId}
          labelElement={<FormattedMessage defaultMessage="Include heatmap" key="checkbox-label-include-heatmap" />}
          onChange={() => dispatch(toggleHeatmapStyleId(styleId))}
          onClick={event => event.stopPropagation()}
          selected={heatmapStyleIds.includes(styleId)}
        />
      );
    }
    return null;
  }, [isAdmin, isPLP, dispatch, dynamicStyleIds, heatmapStyleIds, styleId]);

  const { activitySelectOptions } = useActivityStrings();

  const activitySelect = useMemo(() => {
    if (styleId === 'recordingsAll' || styleId === 'recordingsPop') {
      return (
        <Select
          className={styles.activitySelect}
          hideLabel
          labelText={intl.formatMessage({ defaultMessage: 'Activity type' })}
          options={[{ label: <FormattedMessage defaultMessage="All activity types" />, value: 'all' }, ...activitySelectOptions]}
          onChange={value =>
            dispatch(updateAdminStyleSettings({ ...adminStyleSettings, activityType: value === 'all' ? 'all' : (value as ActivityType) }))
          }
          onClick={e => e.stopPropagation()}
          placeholder=""
          size="sm"
          value={adminStyleSettings?.activityType}
        />
      );
    }
    return null;
  }, [activitySelectOptions, adminStyleSettings, dispatch, intl, styleId]);

  const boundaryAssignmentMultiselect = useMemo(() => {
    if (styleId === 'geoBoundaries') {
      const optionTitles = {
        [BoundaryFilterType.osm]: intl.formatMessage({ defaultMessage: 'OSM Parks' }),
        [BoundaryFilterType.alertClosure]: intl.formatMessage({ defaultMessage: 'Alerts: Closure' }),
        [BoundaryFilterType.alertNonClosure]: intl.formatMessage({ defaultMessage: 'Alerts: Danger, Caution, Info' }),
        [BoundaryFilterType.groupPermissions]: intl.formatMessage({ defaultMessage: 'Permissions' })
      };
      const filterTypeIsVisible = (filter: BoundaryFilterType): boolean => {
        switch (filter) {
          case BoundaryFilterType.osm:
            return hasPermission({ permission: 'areas:manage' });
          case BoundaryFilterType.alertClosure:
          case BoundaryFilterType.alertNonClosure:
            return hasPermission({ permission: 'alerts:manage' }) || isPLP;
          case BoundaryFilterType.groupPermissions:
            return hasPermission({ permission: 'public_lands_onboarding:manage' });
          default:
            return false;
        }
      };
      const filterTypes = Object.values(BoundaryFilterType).filter(filterTypeIsVisible);
      const options = filterTypes.map(filter => ({
        selected: adminStyleSettings?.boundaryFilters?.includes(filter),
        title: optionTitles[filter],
        onClick: () => dispatch(addOrRemoveBoundaryFilter(filter))
      }));

      const boundaryFilterCount = adminStyleSettings?.boundaryFilters?.length ?? 0;
      const showAllLabel = boundaryFilterCount === 0 || boundaryFilterCount === filterTypes.length;

      return (
        <DropdownMenu
          options={options}
          placement="bottom-start"
          anchor={
            <Dropdown
              className={styles.filterMultiSelect}
              text={
                <FormattedMessage
                  defaultMessage="{filterCount, plural, =0 {All} one {# Filter} other {# Filters}}"
                  values={{ filterCount: showAllLabel ? 0 : boundaryFilterCount }}
                />
              }
              variant="secondary"
              size="sm"
              onClick={() => setBoundaryTypeSelectOpen(!boundaryTypeSelectOpen)}
              aria-expanded={boundaryTypeSelectOpen}
              aria-haspopup="menu"
              width="full"
            />
          }
          isOpen={boundaryTypeSelectOpen}
          onCloseRequest={() => setBoundaryTypeSelectOpen(false)}
        />
      );
    }
    return null;
  }, [styleId, intl, hasPermission, isPLP, adminStyleSettings?.boundaryFilters, boundaryTypeSelectOpen, dispatch]);

  const communityWaypointCategoryFilters = useMemo(() => {
    if (active && styleId === 'communityWaypoints') {
      return <CommunityWaypointCategoryFilters />;
    }
    return null;
  }, [active, styleId]);

  const keyboardShortcutText = useMemo(() => {
    const keycode = keycodeShortcuts[styleId];

    if (keycode && isAdmin) {
      return `Opt+${keycode.replace('Key', '')}`; // KeyA -> A
    }
    return undefined;
  }, [isAdmin, styleId]);

  const isDisabled = useMemo(() => {
    if (isAdmin) {
      if (styleId === adminStyleSourceUrlId && !adminUrlIsValid(adminStyleSettings?.styleSourceUrls?.[adminStyleSourceUrlId!])) {
        return true;
      }
    }
    if (styleId === 'surfaceType' && dynamicStyleIds.includes('groundConditions')) {
      // While groundConditions is applied, do not let users edit surfaceType
      return true;
    }
    return false;
  }, [adminStyleSettings?.styleSourceUrls, dynamicStyleIds, isAdmin, styleId]);

  const icon = useExtraMenuItemIcon(styleId);

  const urlButton = useMemo(() => {
    const requiresUrl = styleId === adminStyleSourceUrlId;
    const hasCustomOverlayUrl = !!adminStyleSettings?.styleSourceUrls?.[adminStyleSourceUrlId!];

    if (requiresUrl && onUrlButtonClick) {
      return (
        <Button
          className={styles.urlButton}
          onClick={onUrlButtonClick}
          size="sm"
          stopPropagation
          text={
            hasCustomOverlayUrl ? <FormattedMessage defaultMessage="Edit custom overlay" /> : <FormattedMessage defaultMessage="Add custom overlay" />
          }
        />
      );
    }
    return null;
  }, [adminStyleSettings?.styleSourceUrls, onUrlButtonClick, styleId]);

  return (
    <div className={classNames(styles.container, { [styles.adminContainer]: isAdmin })} ref={itemRef}>
      <div className={styles.containerInner}>
        <div className={styles.iconBackground}>
          {icon ?? <Image className={styles.image} alt={intl.formatMessage({ defaultMessage: 'Preview' })} src={imgSrc} width={30} height={36} />}
        </div>
        <Switch
          disabled={isDisabled}
          labelText={displayName}
          onChange={() => onStyleSelect(styleId)}
          selected={active}
          subText={!isMobile && keyboardShortcutText ? keyboardShortcutText : undefined}
          width="full"
        />
      </div>
      {(activitySelect || boundaryAssignmentMultiselect || heatmapCheckbox || urlButton || communityWaypointCategoryFilters) && (
        <div className={styles.extraControls}>
          {activitySelect}
          {boundaryAssignmentMultiselect}
          {heatmapCheckbox}
          {urlButton}
          {communityWaypointCategoryFilters}
        </div>
      )}
    </div>
  );
};

export default ExtraMenuItem;
